import React, { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '@center-pwa/store/store';
import { setUserId, setFavoriteUser } from '@center-pwa/reducers/userProfileReducer';
import { selectAllMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import { UserIdentityProvider } from 'design-system/my-lib';

/**
 * Bridges the Redux store and react-router to the UserIdentityContext
 * so the UserIdentity design system component can access app-specific state.
 */
export const UserIdentityReduxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userId = useSelector((state: RootState) => state.userProfile?.userId) ?? null;
  const identity = useSelector((state: RootState) => state.userProfile?.identity) ?? null;
  const metaspaces = useSelector(selectAllMetaspaces) ?? null;

  // The identity id and display name come from the store, where app initialization
  // puts the identity `/auth/bootstrap` verified. The Descope session's `claims` object
  // is not the decoded JWT and has no `sub`, so it is never read for the identity.
  const identityId = useSelector((state: RootState) => state.userProfile?.identityId) ?? null;
  const displayName = useSelector((state: RootState) => state.userProfile?.displayName) ?? null;

  const handleSetUserId = useCallback(
    (id: string) => {
      dispatch(setUserId({ userId: id }));
    },
    [dispatch]
  );

  const handleSetFavoriteUser = useCallback(
    (id: string) => {
      dispatch(setFavoriteUser({ userId: id }));
    },
    [dispatch]
  );

  const handleNavigate = useCallback(
    (path: string) => {
      navigate(path);
    },
    [navigate]
  );

  return (
    <UserIdentityProvider
      value={{
        userId,
        identityId,
        displayName,
        identity,
        metaspaces,
        setUserId: handleSetUserId,
        setFavoriteUser: handleSetFavoriteUser,
        navigate: handleNavigate,
      }}
    >
      {children}
    </UserIdentityProvider>
  );
};
